import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, LogOut, FileWarning } from 'lucide-react';
import { startIdleWarning, warnMsFromPolicy } from '../lib/sessionLifecycle';
import { useNavHistory } from '../lib/NavHistory';
import {
  overlayStyle, cardWrap, btnPrimary, btnDanger, neuShadow,
} from '../pages/crm/crmShared';

/**
 * Idle-timeout warning modal — Task #215, reworked for P110 (24 Oct 2026).
 *
 * Staff feedback F1 (Luisa): "the system locks or logs me out very quickly."
 * Daniel's decision: the staff inactivity timeout moves 15 → 30 minutes and a
 * warning appears 2 minutes before logout. Automatic logoff itself stays —
 * HIPAA §164.312(a)(2)(iv)/(a)(2)(iii) — only its length changes.
 *
 * ⚠ EVERY NUMBER ON THIS SCREEN COMES FROM THE SERVER. The component reads
 * GET /api/session-policy via window.zignagenixAPI.onSessionPolicy() and
 * derives the warning threshold with warnMsFromPolicy(). It holds no timeout
 * constant of its own: a dialog promising "2:00" over a server that expires
 * sooner is a worse bug than no dialog at all.
 *
 * Mount only while a user is logged in (App.js renders it inside the
 * authenticated branch). Unmounting tears down the idle listener via
 * the useEffect cleanup.
 */
export default function IdleWarningModal({ onLogout }) {
  const [policy, setPolicy] = useState(null);
  const [show, setShow] = useState(false);
  // ⚠ null, NOT 0. The logout effect below fires at zero, and effects in the
  // same commit see the PRE-update value — so a 0 initial meant the dialog
  // signed the user out on the very render that opened it, while still
  // painting "2:00". Measured by the countdown arm in
  // tests/react/idleWarningDialog.test.js.
  const [msLeft, setMsLeft] = useState(null);
  const [dirtyCount, setDirtyCount] = useState(0);
  const handleRef = useRef(null);
  const stayRef = useRef(null);
  // ⚠ Held in a ref, NOT read as an effect dependency. useNavHistory() returns
  // a FRESH object on every render when no provider is mounted, so depending on
  // unsavedWorkCount's identity re-ran the countdown effect on every tick and
  // pinned the deadline at its starting value — the clock showed 2:00 forever
  // and never reached 0. Measured by the countdown arm in
  // tests/react/idleWarningDialog.test.js.
  const nav = useNavHistory();
  const navRef = useRef(nav);
  navRef.current = nav;

  // ── The server's policy ────────────────────────────────────────────────
  useEffect(() => {
    const off = window.zignagenixAPI?.onSessionPolicy?.((p) => setPolicy(p));
    return () => { if (typeof off === 'function') off(); };
  }, []);

  // ── Idle clock. Armed only once the policy is known (see note above). ──
  useEffect(() => {
    const warnMs = warnMsFromPolicy(policy);
    if (!warnMs) return undefined;
    const handle = startIdleWarning({
      warnMs,
      onWarn: () => setShow(true),
      onReset: () => setShow(false),
    });
    handleRef.current = handle;
    return () => {
      handle.stop();
      handleRef.current = null;
    };
  }, [policy]);

  // ── A peer tab clicked "Stay signed in" — dismiss ours too (P110 c). ───
  useEffect(() => {
    window.zignagenixAPI?.onPeerKeepAlive?.(() => {
      handleRef.current?.reset();
      setShow(false);
    });
  }, []);

  // ── Countdown. Driven off a wall-clock deadline, not an accumulating
  // tick: a background/throttled tab resumes with the RIGHT time remaining
  // instead of a count that silently fell behind the server's clock. ──────
  useEffect(() => {
    if (!show || !policy) return undefined;
    const deadline = Date.now() + policy.warnBeforeMs;
    setMsLeft(policy.warnBeforeMs);
    const count = navRef.current?.unsavedWorkCount;
    setDirtyCount(typeof count === 'function' ? count() : 0);
    const tick = setInterval(() => {
      setMsLeft(Math.max(0, deadline - Date.now()));
    }, 250);
    return () => clearInterval(tick);
  }, [show, policy]);

  // At 0: the real hard-logout. onLogout → handleLogout → auth:logout, which
  // revokes the SERVER session (deleteSession), clears PHI from the screen by
  // unmounting the authenticated tree, and wipes the client token cache.
  useEffect(() => {
    if (show && policy && msLeft !== null && msLeft <= 0) onLogout('idle_timeout');
  }, [show, msLeft, policy, onLogout]);

  const handleKeepAlive = useCallback(async () => {
    handleRef.current?.reset();
    setShow(false);
    window.zignagenixAPI?.reportActivity();
    // (c) Any sibling tab showing the same dialog dismisses it and adopts the
    // new activity time, instead of counting down to a logout that is no
    // longer coming.
    window.zignagenixAPI?.broadcastKeepAlive?.();
    try {
      // A REAL server keep-alive, not just a client reset. Any /api/ipc call
      // runs the server middleware's touchSession(), which rewrites
      // active_sessions.last_activity — so the SERVER's idle clock restarts,
      // not only this tab's. getSession() is the lightest available handler.
      await window.zignagenixAPI?.getSession();
    } catch {
      // Network error: reset() above already dismissed the modal and restarted
      // the client-side clock, so the user isn't trapped. The server's own
      // clock is then whatever the last successful request set it to.
    }
  }, []);

  // ── Keyboard accessibility ─────────────────────────────────────────────
  // Focus moves to "Stay signed in" — the SAFE action — as soon as the dialog
  // opens, so a keyboard user lands on it without hunting.
  //
  // Escape also means "stay": Escape must never be the gesture that signs a
  // clinician out mid-note. It runs the full keep-alive (server call included)
  // rather than only dismissing the dialog.
  //
  // ⚠ THERE IS DELIBERATELY NO TAB FOCUS TRAP. startIdleWarning() listens for
  // `keydown` on window (WARN_EVENTS), so ANY keystroke — Tab included —
  // counts as activity and dismisses this dialog before a trap could run.
  // Measured while writing tests/react/idleWarningDialog.test.js: a trap here
  // was unreachable code, and the test asserting it only passed because the
  // dialog had already unmounted. The reachable property is asserted instead:
  // a keystroke dismisses the warning and the session continues.
  useEffect(() => {
    if (!show) return undefined;
    stayRef.current?.focus();
    function onKeyDown(e) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      handleKeepAlive();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [show, handleKeepAlive]);

  if (!show || !policy) return null;

  const totalSeconds = Math.ceil((msLeft === null ? policy.warnBeforeMs : msLeft) / 1000);
  const mm = Math.floor(totalSeconds / 60);
  const ss = String(totalSeconds % 60).padStart(2, '0');
  const clock = `${mm}:${ss}`;
  const urgent = totalSeconds <= 30;

  return (
    <div style={{ ...overlayStyle, zIndex: 9999 }}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-warn-title"
        aria-describedby="idle-warn-body"
        style={{
          ...cardWrap,
          width: 420,
          padding: 32,
          boxShadow: '12px 12px 24px rgba(186,199,216,0.65), -12px -12px 24px rgba(255,255,255,0.9)',
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: urgent ? 'linear-gradient(135deg, #FEE2E2, #FECACA)' : 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: neuShadow,
          transition: 'background 0.3s ease',
        }}>
          <Clock size={26} color={urgent ? '#DC2626' : '#D97706'} />
        </div>

        <h2 id="idle-warn-title" style={{ fontSize: 17, fontWeight: 700, color: '#212529', margin: '0 0 10px' }}>
          Still there?
        </h2>

        {/* The countdown is announced politely rather than assertively: a
            screen reader re-reading every second would drown out the buttons. */}
        <p id="idle-warn-body" style={{ fontSize: 13, color: '#6C757D', lineHeight: 1.6, margin: '0 0 6px' }}>
          You'll be signed out in{' '}
          <strong
            aria-live="polite"
            aria-atomic="true"
            style={{ color: urgent ? '#DC2626' : '#212529', fontVariantNumeric: 'tabular-nums', transition: 'color 0.3s ease' }}
          >
            {clock}
          </strong>{' '}
          for security.
        </p>

        {/* (d) Unsaved work is NAMED, never silently auto-saved — writing a
            half-finished clinical note to the database on a timer would be a
            worse outcome than losing the draft. Count only, no field text. */}
        {dirtyCount > 0 && (
          <p style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 12.5, color: '#B45309', background: '#FFFBEB',
            border: '1px solid #FDE68A', borderRadius: 8,
            padding: '8px 10px', margin: '12px 0 0', lineHeight: 1.5,
          }}>
            <FileWarning size={14} style={{ flexShrink: 0 }} />
            <span>
              {dirtyCount > 1
                ? 'You have unsaved changes in more than one place. They will be lost — they are not saved automatically.'
                : 'You have unsaved changes. They will be lost — they are not saved automatically.'}
            </span>
          </p>
        )}

        <p style={{ fontSize: 12, color: '#ADB5BD', margin: '14px 0 24px' }}>
          Sessions end after {policy.idleTimeoutMinutes} minutes of inactivity.
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            ref={stayRef}
            type="button"
            onClick={handleKeepAlive}
            style={{ ...btnPrimary, fontSize: 13, padding: '10px 22px' }}
          >
            Stay signed in
          </button>
          <button
            type="button"
            onClick={() => onLogout('signout')}
            style={{ ...btnDanger, fontSize: 13, padding: '10px 22px' }}
          >
            <LogOut size={13} /> Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
